/**
 * Conversational state for the EXPAI agent (Lotes 1-6 training, §4.1-4.2).
 *
 * This module is deliberately framework-agnostic so it can be unit-tested
 * head-less (Vitest) and reused from any pipeline layer (Context Reader,
 * Reference Resolver, Recommendation Engine).
 */

export type EntityType =
  | 'camera'
  | 'edge'
  | 'plc'
  | 'workOrder'
  | 'line'
  | 'unknown';

export interface EntityRef {
  type: EntityType;
  id: string;
  label?: string;
}

export interface RecentEntity extends EntityRef {
  ts: number;
}

export interface ActiveView {
  entityType: EntityType | 'system';
  entityId: string | null;
  subView: string | null; // e.g. "health" | "kpis" | "timeline"
  route: string;
}

export type ConversationGoal =
  | 'troubleshooting'
  | 'reporting'
  | 'exploring'
  | 'comparing'
  | 'unknown';

export type UserRole =
  | 'admin'
  | 'supervisor'
  | 'operator'
  | 'qualityEngineer'
  | 'mlOpsEngineer';

export interface TurnRecord {
  ts: number;
  userInput: string;
  intent: string | null;
  toolCalled: string | null;
  slots: Record<string, unknown>;
  uiActions: string[];
  agentReply: string;
}

export interface PendingRecommendation {
  id: string; // REC-XX
  message: string;
  intent: string;
  slots: Record<string, unknown>;
  priority: 'high' | 'medium' | 'low';
  shownAt: number; // epoch ms — used for the 60s auto-dismiss
}

export interface ConversationContext {
  activeEntity: EntityRef | null;
  recentEntities: RecentEntity[]; // LRU max 5, newest first
  activeView: ActiveView | null;
  appliedFilters: Record<string, unknown>;
  conversationGoal: ConversationGoal;
  userRole: UserRole;
  turnHistory: TurnRecord[];
  pendingRecommendation: PendingRecommendation | null;
  /** Internal — last recalculation tick of conversationGoal */
  goalRecomputedAt: number;
  /** Internal — REC-IDs dismissed by user (id -> ts) for 30 min suppression */
  dismissedRecommendations: Record<string, number>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
export const RECENT_MAX = 5;
export const ACTIVE_ENTITY_TTL_MS = 30 * 60 * 1000; // 30 min
export const REC_SUPPRESSION_MS = 30 * 60 * 1000;
export const REC_AUTO_DISMISS_MS = 60 * 1000;
export const GOAL_RECOMPUTE_EVERY = 3; // turns

const SYSTEM_VIEWS: ReadonlySet<string> = new Set([
  'compare',
  'network-topology',
  'floor-map',
  'sla',
]);

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------
export function createConversationContext(
  overrides: Partial<ConversationContext> = {},
): ConversationContext {
  return {
    activeEntity: null,
    recentEntities: [],
    activeView: null,
    appliedFilters: {},
    conversationGoal: 'unknown',
    userRole: 'supervisor',
    turnHistory: [],
    pendingRecommendation: null,
    goalRecomputedAt: 0,
    dismissedRecommendations: {},
    ...overrides,
  };
}

// ---------------------------------------------------------------------------
// Active-entity update rules (§4.2)
// ---------------------------------------------------------------------------
function isSystemSubView(subView: string | null): boolean {
  return !!subView && SYSTEM_VIEWS.has(subView);
}

/**
 * Decide whether a navigation should mutate `activeEntity`.
 * Per §4.2: only entity-detail navigations update activeEntity; systemic
 * views (compare, topology, floor-map, sla) do not.
 */
export function shouldUpdateActiveEntity(view: ActiveView): boolean {
  if (!view.entityId) return false;
  if (view.entityType === 'system') return false;
  if (isSystemSubView(view.subView)) return false;
  return true;
}

function pushRecent(
  list: RecentEntity[],
  ref: EntityRef,
  ts: number,
): RecentEntity[] {
  const filtered = list.filter(
    (r) => !(r.type === ref.type && r.id === ref.id),
  );
  const next: RecentEntity[] = [{ ...ref, ts }, ...filtered];
  return next.slice(0, RECENT_MAX);
}

/**
 * Filters that travel between views. A range filter on WO does not transfer
 * to a CAM view (§4.2). This list intentionally keeps cross-cutting filters
 * only.
 */
const TRANSFERABLE_FILTERS: ReadonlySet<string> = new Set([
  'line',
  'lineId',
]);

export function inheritFilters(
  prev: Record<string, unknown>,
  nextEntityType: EntityType | 'system',
  prevEntityType: EntityType | 'system' | null,
): Record<string, unknown> {
  if (prevEntityType === nextEntityType) return { ...prev };
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(prev)) {
    if (TRANSFERABLE_FILTERS.has(k)) out[k] = v;
  }
  return out;
}

// ---------------------------------------------------------------------------
// updateContext: apply a turn's outcome to the context
// ---------------------------------------------------------------------------
export interface TurnOutcome {
  userInput: string;
  intent: string | null;
  toolCalled: string | null;
  slots?: Record<string, unknown>;
  uiActions?: string[];
  agentReply?: string;
  navigatedTo?: ActiveView;
  filtersApplied?: Record<string, unknown>;
  ts?: number;
}

export function updateContext(
  ctx: ConversationContext,
  outcome: TurnOutcome,
): ConversationContext {
  const ts = outcome.ts ?? Date.now();
  const next: ConversationContext = {
    ...ctx,
    recentEntities: [...ctx.recentEntities],
    appliedFilters: { ...ctx.appliedFilters },
    turnHistory: ctx.turnHistory.slice(-49), // cap
    dismissedRecommendations: { ...ctx.dismissedRecommendations },
  };

  // 1. Active entity TTL eviction
  if (
    next.activeEntity &&
    ctx.turnHistory.length > 0 &&
    ts - (ctx.turnHistory[ctx.turnHistory.length - 1]?.ts ?? ts) >
      ACTIVE_ENTITY_TTL_MS
  ) {
    next.activeEntity = null;
  }

  // 2. Navigation
  if (outcome.navigatedTo) {
    const view = outcome.navigatedTo;
    next.appliedFilters = inheritFilters(
      next.appliedFilters,
      view.entityType,
      next.activeView?.entityType ?? null,
    );
    next.activeView = view;

    if (shouldUpdateActiveEntity(view)) {
      const ref: EntityRef = {
        type: view.entityType as EntityType,
        id: view.entityId!,
        label: view.entityId!,
      };
      next.activeEntity = ref;
      next.recentEntities = pushRecent(next.recentEntities, ref, ts);
    }
  }

  // 3. Filters
  if (outcome.filtersApplied) {
    next.appliedFilters = { ...next.appliedFilters, ...outcome.filtersApplied };
  }

  // 4. Turn record
  next.turnHistory = [
    ...next.turnHistory,
    {
      ts,
      userInput: outcome.userInput,
      intent: outcome.intent,
      toolCalled: outcome.toolCalled,
      slots: outcome.slots ?? {},
      uiActions: outcome.uiActions ?? [],
      agentReply: outcome.agentReply ?? '',
    },
  ];

  // 5. Goal recompute every N turns
  if (next.turnHistory.length - next.goalRecomputedAt >= GOAL_RECOMPUTE_EVERY) {
    next.conversationGoal = inferConversationGoal(next.turnHistory);
    next.goalRecomputedAt = next.turnHistory.length;
  }

  return next;
}

// ---------------------------------------------------------------------------
// Goal inference (§5.3)
// ---------------------------------------------------------------------------
const GOAL_KEYWORDS: Record<Exclude<ConversationGoal, 'unknown' | 'exploring'>, RegExp> = {
  troubleshooting:
    /\b(warning|alerta|alarma|problema|qu[eé]\s+pasa|est[áa]\s+bien|salud|issue|fail|fallo|critical|cr[íi]tic|diagn[óo]stic)/i,
  reporting:
    /\b(compliance|sla|reporte|informe|exportar|hist[óo]rico|auditor[íi]a|report|export)/i,
  comparing: /\b(comparar?|vs|diferencia\s+entre|mejor|peor|compare|versus)/i,
};

export function inferConversationGoal(
  history: TurnRecord[],
): ConversationGoal {
  const last = history.slice(-3);
  if (last.length === 0) return 'unknown';
  const text = last.map((t) => t.userInput).join(' • ').toLowerCase();

  // Priority: troubleshooting > comparing > reporting > exploring
  if (GOAL_KEYWORDS.troubleshooting.test(text)) return 'troubleshooting';
  if (GOAL_KEYWORDS.comparing.test(text)) return 'comparing';
  if (GOAL_KEYWORDS.reporting.test(text)) return 'reporting';

  // exploring: 2+ distinct entity types in window without depth
  const types = new Set(
    last
      .map((t) => (t.slots?.entityType as string) ?? null)
      .filter(Boolean) as string[],
  );
  if (types.size >= 2) return 'exploring';

  return 'unknown';
}

// ---------------------------------------------------------------------------
// Dismissal helpers (Recommendation Engine consumes these)
// ---------------------------------------------------------------------------
export function isRecDismissed(
  ctx: ConversationContext,
  recId: string,
  now = Date.now(),
): boolean {
  const ts = ctx.dismissedRecommendations[recId];
  if (!ts) return false;
  return now - ts < REC_SUPPRESSION_MS;
}

export function dismissRec(
  ctx: ConversationContext,
  recId: string,
  now = Date.now(),
): ConversationContext {
  return {
    ...ctx,
    dismissedRecommendations: {
      ...ctx.dismissedRecommendations,
      [recId]: now,
    },
    pendingRecommendation: null,
  };
}
